import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { cn } from '@epoch-ui/lib/utils';

const buttonVariants = cva(
  'tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-ring-offset-background tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-offset-1 disabled:tw-pointer-events-none disabled:tw-opacity-50 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-offset-1 hover:tw-brightness-95',
  {
    variants: {
      variant: {
        primary:
          'tw-bg-primary tw-text-primary-foreground focus-visible:tw-ring-ring focus:tw-ring-ring',
        secondary:
          'tw-bg-secondary tw-text-secondary-foreground focus-visible:tw-ring-input focus:tw-ring-input',
        outline:
          'tw-border tw-border-solid tw-border-input tw-bg-background focus-visible:tw-ring-input focus:tw-ring-input',
        destructive:
          'tw-bg-destructive tw-text-destructive-foreground focus-visible:tw-ring-destructive-foreground focus:tw-ring-destructive-foreground',
        ghost:
          'hover:tw-bg-accent hover:tw-text-accent-foreground focus-visible:tw-ring-transparent focus:tw-ring-transparent',
        link: 'tw-text-primary tw-underline-offset-4 tw-underline focus-visible:tw-ring-transparent focus:tw-ring-transparent focus-visible:tw-ring-offset-transparent focus:tw-ring-offset-transparent'
      },
      size: {
        none: '',
        lg: 'tw-h-12 tw-px-4 tw-py-4',
        sm: 'tw-h-10 tw-rounded-md tw-px-3',
        xs: 'tw-h-[38px] tw-rounded-md tw-px-3',
        xxs: 'tw-h-8 tw-rounded-md tw-px-3 tw-py-2'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      type = 'button',
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        data-loading={loading}
        {...props}
      >
        {loading ? (
          <span className="tw-relative tw-flex tw-items-center tw-justify-center">
            <FontAwesomeIcon className="tw-absolute" icon={faSpinner} spin />
            <span className="tw-opacity-0">{children}</span>
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
